import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';
import HeroSection from './components/HeroSection';
import CalculatorForm from './components/CalculatorForm';
import type { CalculatorInputs } from './components/CalculatorForm';
import ResultsPanel from './components/ResultsPanel';
import AssumptionsPanel from './components/AssumptionsPanel';
import LeadCaptureForm from './components/LeadCaptureForm';
import ReportDownloadForm from './components/ReportDownloadForm';

export default function RoiCalculatorPage() {
  const [results, setResults] = useState<CalculatorInputs | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "ROI Calculator for Precision Agriculture",
    "description": "Calculate potential savings and yield improvements from switching to precision aerial application",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Any"
  };

  const handleCalculate = (inputs: CalculatorInputs) => {
    setResults({ ...inputs });
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      <SEO
        title="ROI Calculator - Estimate Your Savings | Greenfield Partners"
        description="Calculate potential savings and yield improvements from switching to precision aerial application. See how much you could recover per acre."
        keywords="agricultural ROI calculator, drone spraying savings, yield improvement calculator, precision agriculture ROI"
        schema={schema}
      />
      <HeroSection />

      {/* Trust Bar */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-2xl font-bold text-emerald-800">50,000+</div>
              <p className="text-xs text-gray-500 font-medium mt-1">Acres Served Annually</p>
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-800">5–15%</div>
              <p className="text-xs text-gray-500 font-medium mt-1">Average Yield Improvement</p>
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-800">$8–$15</div>
              <p className="text-xs text-gray-500 font-medium mt-1">Saved Per Acre Per Pass</p>
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-800">80%</div>
              <p className="text-xs text-gray-500 font-medium mt-1">Less Compaction Damage</p>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
              Estimate Your Savings
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-wide">
              Enter Your Farm Details
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto font-light leading-relaxed">
              Adjust the inputs below to see how much yield and revenue you could recover. All estimates are based on USDA data and regional field observations.
            </p>
          </div>

          <div className="grid lg:grid-cols-[420px_1fr] gap-10 items-start">
            {/* Left: Form + Assumptions */}
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <CalculatorForm onCalculate={handleCalculate} />
              </div>
              <AssumptionsPanel />
            </div>

            {/* Right: Results or Placeholder */}
            <div>
              {results ? (
                <div className="space-y-8">
                  <ResultsPanel inputs={results} />
                  <LeadCaptureForm
                    acreage={results.acreage}
                    cropType={results.cropType}
                    annualSavings={results.annualSavings}
                  />
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg p-10 text-center">
                  <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-emerald-50 rounded-full">
                    <i className="ri-calculator-line text-4xl text-emerald-700"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Your Results Will Appear Here</h3>
                  <p className="text-sm text-gray-500 max-w-sm mx-auto leading-relaxed">
                    Fill in your farm details on the left and hit &quot;Calculate My Savings&quot; to see a personalized breakdown of potential yield recovery and cost savings.
                  </p>

                  {/* Preview cards */}
                  <div className="mt-8 grid grid-cols-3 gap-3">
                    <div className="bg-stone-50 rounded-lg p-4">
                      <div className="h-8 w-24 mx-auto bg-stone-200/60 rounded mb-2"></div>
                      <p className="text-xs text-gray-400">Annual Recovery</p>
                    </div>
                    <div className="bg-stone-50 rounded-lg p-4">
                      <div className="h-8 w-16 mx-auto bg-stone-200/60 rounded mb-2"></div>
                      <p className="text-xs text-gray-400">ROI %</p>
                    </div>
                    <div className="bg-stone-50 rounded-lg p-4">
                      <div className="h-8 w-20 mx-auto bg-stone-200/60 rounded mb-2"></div>
                      <p className="text-xs text-gray-400">Net Savings</p>
                    </div>
                  </div>

                  {/* Red callout teaser */}
                  <div className="mt-8 border-l-3 border-red-500 bg-red-50/60 rounded-r-lg px-4 py-3 text-left">
                    <p className="text-sm text-gray-700">
                      Most Mid-Atlantic operations lose <strong className="text-red-600">$40–$130+ per acre</strong> annually to preventable issues like compaction, missed spray windows, and deer pressure.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 px-6 topo-texture-light bg-gradient-to-b from-stone-100/40 via-stone-100/70 to-stone-100/40">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-14">
            <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
              Side by Side
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-wide">
              Ground Sprayer vs. Precision Aerial
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Ground */}
            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg">
                  <i className="ri-truck-line text-xl text-gray-500"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800">Traditional Ground Sprayer</h3>
              </div>
              <ul className="space-y-3">
                {[
                  '3–5% crop destruction per pass from wheel traffic',
                  'Soil compaction reduces root depth up to 40%',
                  "Can't enter wet fields — delays cost yield",
                  'Breaks stalks in tall corn (V8+)',
                  'Multiple passes multiply damage',
                  'Ruts create drainage problems for next season',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-close-circle-line text-red-400"></i>
                    </div>
                    <span className="text-sm text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-5 border-t border-gray-100">
                <div className="text-center">
                  <span className="text-2xl font-bold text-red-600">$27K–$45K</span>
                  <p className="text-xs text-gray-500 mt-1">Lost per 1,000 acres per season</p>
                </div>
              </div>
            </div>

            {/* Aerial */}
            <div className="bg-white rounded-lg p-8 border-2 border-emerald-600 relative">
              <div className="absolute -top-3 right-6 bg-emerald-700 text-white text-xs font-bold px-3 py-1 rounded-full">
                Recommended
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-lg">
                  <i className="ri-drone-line text-xl text-emerald-700"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800">Precision Aerial Application</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Zero wheel traffic — no crop damage, no compaction',
                  "Apply during wet conditions when ground rigs can't",
                  'Hit critical spray windows on time, every time',
                  'No broken stalks, no stand loss',
                  'Uniform coverage with GPS precision',
                  'Healthier soil structure season after season',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-checkbox-circle-line text-emerald-600"></i>
                    </div>
                    <span className="text-sm text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-5 border-t border-emerald-100">
                <div className="text-center">
                  <span className="text-2xl font-bold text-emerald-800">5–15%</span>
                  <p className="text-xs text-gray-500 mt-1">Average yield improvement</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real Farmer Examples */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
              Real Examples
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-wide">
              What Farmers Like You Are Saving
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These are real operations seeing measurable results. <a href="/case-studies" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">View more case studies <i className="ri-arrow-right-line"></i></a>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://public.readdy.ai/ai/img_res/327cd0cecd61891c312e169a3c8f3f06.jpg"
                  alt="Mark H."
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-gray-900 text-sm">Mark H.</p>
                  <p className="text-xs text-gray-500">2,400 acres · Corn &amp; Soybeans</p>
                </div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-4 text-center mb-4">
                <div className="text-2xl font-bold text-emerald-800">$38,400</div>
                <p className="text-xs text-gray-500 mt-1">Estimated annual savings</p>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Eliminated wheel traffic on 2,400 acres of corn and soybeans. Recovered an estimated 4% crop loss per pass and hit every fungicide window on time.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20female%20farmer%20in%20her%20fifties%20wearing%20plaid%20shirt%20outdoors%20natural%20lighting%20warm%20smile%20confident%20expression%20rural%20Wisconsin%20farmer%20authentic%20documentary%20style%20photography&width=120&height=120&seq=farmer-janet-001&orientation=squarish"
                  alt="Janet K."
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-gray-900 text-sm">Janet K.</p>
                  <p className="text-xs text-gray-500">1,100 acres · Corn &amp; Wheat</p>
                </div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-4 text-center mb-4">
                <div className="text-2xl font-bold text-emerald-800">$84,200</div>
                <p className="text-xs text-gray-500 mt-1">Estimated annual savings</p>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Combined aerial application with deer deterrent on edge rows. Cut browse losses by over 50% and eliminated compaction across all fields.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20older%20male%20farmer%20in%20his%20sixties%20wearing%20baseball%20cap%20and%20work%20jacket%20outdoors%20natural%20lighting%20serious%20trustworthy%20expression%20Iowa%20farmer%20authentic%20documentary%20style%20photography&width=120&height=120&seq=farmer-tom-001&orientation=squarish"
                  alt="Tom R."
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-gray-900 text-sm">Tom R.</p>
                  <p className="text-xs text-gray-500">3,800 acres · Soybeans &amp; Corn</p>
                </div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-4 text-center mb-4">
                <div className="text-2xl font-bold text-emerald-800">$152,000</div>
                <p className="text-xs text-gray-500 mt-1">Estimated annual savings</p>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Full-service package across 3,800 acres. Aerial application, field assessment, and timely fungicide windows recovered significant yield across the operation.
              </p>
            </div>
          </div>
        </div>
      </section>

      <ReportDownloadForm />

      {/* CTA */}
      <div className="h-40 bg-gradient-to-b from-white via-stone-200/40 to-[#1a1f1c]"></div>
      <section className="py-20 px-6 topo-cta-bg text-white relative">
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-wide">
            Ready to Protect Your Yield?
          </h2>
          <p className="text-lg text-gray-300 mb-8 font-light leading-relaxed">
            No pressure. No sales pitch. Just a practical conversation about your fields and whether our services make sense for your operation. Want to see pricing first? <a href="/instant-quote" className="text-white hover:text-emerald-200 font-semibold underline">Get an instant quote</a> or explore our <a href="/seasonal-programs" className="text-white hover:text-emerald-200 font-semibold underline">seasonal programs</a>. Need financing? <a href="/financing" className="text-white hover:text-emerald-200 font-semibold underline">Flexible options available</a>.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+17179131780"
              className="inline-flex items-center gap-2 bg-white text-emerald-900 font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              Call 717-913-1780
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 border-2 border-white/30 text-white font-bold px-8 py-4 rounded-lg hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              Start a Conversation
            </a>
          </div>
        </div>
      </section>

      <div className="h-px bg-white/8"></div>
      <Footer />
    </div>
  );
}
